import { CircleCheck, Clock3, PauseCircle, Timer } from "lucide-react";

import { LiveDot } from "@/components/kb/live-dot";
import type { RuleStatus as ApiRuleStatus } from "@/lib/api";
import { cn } from "@/lib/utils";

export type RuleStatus =
	| { kind: "active" }
	| { kind: "cooling"; until: string }
	| { kind: "paused" }
	| { kind: "live"; nextGame?: string };

export function formatDateTime(value: string): string {
	const date = new Date(value);

	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return new Intl.DateTimeFormat(undefined, {
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		month: "short",
	}).format(date);
}

export function statusFor(
	status: ApiRuleStatus,
	notifyAgainAfter: string | null,
): RuleStatus {
	if (status === "paused") {
		return { kind: "paused" };
	}

	if (notifyAgainAfter && new Date(notifyAgainAfter).getTime() > Date.now()) {
		return { kind: "cooling", until: notifyAgainAfter };
	}

	return { kind: "active" };
}

/**
 * Rule status returned by the API (`active`, `cooling`, or `paused`).
 * Live/scheduled remain represented here for future API support.
 */
export function StatusPill({
	state,
	className,
}: {
	state: RuleStatus;
	className?: string;
}) {
	const base =
		"inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-medium";

	if (state.kind === "paused") {
		return (
			<span
				className={cn(
					base,
					"border border-dashed text-muted-foreground",
					className,
				)}
			>
				<PauseCircle className="size-3.5" />
				Paused
			</span>
		);
	}

	if (state.kind === "live") {
		return (
			<span className={cn(base, "bg-signal-soft text-signal-ink", className)}>
				<LiveDot />
				Tracking live
			</span>
		);
	}

	if (state.kind === "cooling") {
		return (
			<span className={cn(base, "bg-positive-soft text-positive", className)}>
				<Timer className="size-3.5" />
				Quiet until {formatDateTime(state.until)}
			</span>
		);
	}

	if (state.kind === "active") {
		return (
			<span className={cn(base, "bg-muted text-muted-foreground", className)}>
				<CircleCheck className="size-3.5" />
				Ready
			</span>
		);
	}

	return (
		<span className={cn(base, "bg-muted text-muted-foreground", className)}>
			<Clock3 className="size-3.5" />
			Scheduled
		</span>
	);
}
